import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { CheckIcon, CrownIcon } from "lucide-react";
import { type ReactNode, useId } from "react";

import { Button } from "@/shared/ui/button";

import { useProPlanLabels } from "../../lib/useProPlanLabels";

type Row = {
  key: string;
  name: ReactNode;
  // `true` — есть в тарифе (галочка), строка — значение («5 в сутки»).
  free: true | string;
  pro: true | string;
};

type PlanComparisonProps = {
  // Игрок вошёл: «Оформить» открывает окно оплаты, а не вход.
  signedIn: boolean;
  onGetPro: () => void;
};

// Сравнение «Обычная / Pro» по макету design/prototype/ProGuest.dc.html: колонка Pro в золотой
// рамке, цена в шапке, кнопки внизу. Только функции, которые уже работают (rules.md фичи).
// Гостю обе кнопки ведут на вход.
export const PlanComparison = ({ signedIn, onGetPro }: PlanComparisonProps) => {
  const { t } = useLingui();
  const headingId = useId();
  const { price, period, freeLimit } = useProPlanLabels();

  const rows: Row[] = [
    { key: "analyses", name: <Trans>Match analyses</Trans>, free: freeLimit, pro: t`unlimited` },
    { key: "drills", name: <Trans>Drills based on your mistakes</Trans>, free: true, pro: true },
    {
      key: "style",
      name: <Trans>Play style and comparison with friends</Trans>,
      free: true,
      pro: true,
    },
  ];

  const included = (tone: "free" | "pro") => (
    <CheckIcon
      role="img"
      aria-label={t`included`}
      strokeWidth={tone === "pro" ? 2.8 : 2.6}
      className={tone === "pro" ? "mx-auto size-5 text-accent" : "mx-auto size-4.5 text-subtle"}
    />
  );

  return (
    <section
      aria-labelledby={headingId}
      className="@container overflow-x-auto rounded-[14px] border border-border bg-surface pt-4 pb-5"
    >
      <h2 id={headingId} className="sr-only">
        <Trans>Free version and Pro</Trans>
      </h2>
      <table className="mx-3 w-[calc(100%-1.5rem)] border-separate border-spacing-0 text-left @md:mx-5 @md:w-[calc(100%-2.5rem)]">
        <colgroup>
          <col className="w-[44%]" />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <td />
            <th scope="col" className="px-1 pt-4 pb-3.5 text-center align-bottom font-normal">
              <span className="block font-display text-xl leading-tight text-foreground-soft @md:text-2xl">
                <Trans>Free</Trans>
              </span>
              <span className="block text-sm text-subtle">
                <Trans>no charge</Trans>
              </span>
            </th>
            <th
              scope="col"
              className="rounded-t-[14px] border-2 border-b-0 border-accent bg-surface-raised px-1.5 pt-4 pb-3.5 text-center align-bottom font-normal @md:px-2"
            >
              <span className="inline-flex items-center gap-1.5 font-display text-xl leading-tight text-accent @md:gap-2 @md:text-2xl">
                <CrownIcon aria-hidden="true" className="size-5" />
                Pro
              </span>
              <span className="block text-sm">
                <span className="font-mono font-semibold">{price}</span>{" "}
                <span className="text-muted">/ {period}</span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} data-row={row.key}>
              <th
                scope="row"
                className="border-b border-border py-3 pr-2 text-sm font-normal @md:text-[15px]"
              >
                {row.name}
              </th>
              <td className="border-b border-border px-1 py-3 text-center text-sm text-muted">
                {row.free === true ? included("free") : row.free}
              </td>
              <td className="border-x-2 border-b border-x-accent border-b-border-strong bg-surface-raised px-1.5 py-3 text-center text-sm font-semibold text-accent-strong @md:px-2">
                {row.pro === true ? included("pro") : row.pro}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td />
            <td className="px-1 pt-3.5 pb-4">
              {signedIn ? null : (
                <Button
                  asChild
                  variant="outline"
                  className="h-11 w-full border-border px-2 font-semibold text-foreground-soft @md:px-4"
                >
                  <Link to="/login" className="no-underline">
                    <Trans context="pricing button">Sign in</Trans>
                  </Link>
                </Button>
              )}
            </td>
            <td className="rounded-b-[14px] border-2 border-t-0 border-accent bg-surface-raised px-1.5 pt-3.5 pb-4 @md:px-2.5">
              {signedIn ? (
                <Button className="h-11 w-full px-2 font-semibold @md:px-4" onClick={onGetPro}>
                  <Trans>Get Pro</Trans>
                </Button>
              ) : (
                <Button asChild className="h-11 w-full px-2 font-semibold @md:px-4">
                  <Link to="/login" className="no-underline">
                    <Trans>Get Pro</Trans>
                  </Link>
                </Button>
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
};
